"use client"

import type React from "react"

import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import { useEffect, useState, useMemo } from "react"
import type { IValueRating } from "../../../shared/models/three-sixty-feedback-models/ValueRating"
import type { ITemplateRating } from "../../../shared/models/three-sixty-feedback-models/TemplateRating"
import FlagRatingsTable from "./components/FlagRatingsTable"
import {
  Users,
  BarChart3,
  Star,
  Crown,
  Heart,
  Briefcase,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Zap,
} from "lucide-react"
import ModernRatingsTable from "./components/ModernRatingsTable"

interface ILeadershipRatings {
  overallRating: number
  id: string
  rateeId: string
  dimension: string
  isflagged: boolean
  department: string
  description: string
  criteria: {
    [raterId: string]: {
      additionalComment?: string
      stopDoing: string
      startDoing: string
      continueDoing: string
      ratings: {
        [criteriaName: string]: {
          [statement: string]: number
        }
      }
      leadershipScore?: number
    }
  }
  date: number
  raterId?: string
  templateId?: string
  heading?: string
  reasonForRequest?: string
  dueDate?: number
  values?: {}
  archived?: boolean
}

interface RatingCardProps {
  title: string
  subtitle: string
  score: string
  maxScore: number
  icon: React.ReactNode
  gradient: string
  categories: { name: string; score: number; color: string }[]
  loading: boolean
}

const RatingCard: React.FC<RatingCardProps> = ({
  title,
  subtitle,
  score,
  maxScore,
  icon,
  gradient,
  categories,
  loading,
}) => {
  const scoreNum = Number.parseFloat(score) || 0
  const percentage = (scoreNum / maxScore) * 100

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200/50 animate-pulse">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-slate-200 rounded-xl"></div>
          <div className="flex-1">
            <div className="h-5 bg-slate-200 rounded mb-2"></div>
            <div className="h-4 bg-slate-200 rounded w-2/3"></div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="h-8 bg-slate-200 rounded"></div>
          <div className="h-2 bg-slate-200 rounded"></div>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-4 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="group bg-white rounded-2xl p-6 shadow-lg border border-slate-200/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div
          className={`p-3 bg-gradient-to-br ${gradient} rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}
        >
          <div className="text-white">{icon}</div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-slate-800 group-hover:text-slate-900 transition-colors">{title}</h3>
          <p className="text-sm text-slate-600">{subtitle}</p>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-600 group-hover:translate-x-1 transition-all duration-300" />
      </div>

      {/* Score Display */}
      <div className="mb-6">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-3xl font-bold text-slate-800">{score}</span>
          <span className="text-lg text-slate-500">/ {maxScore}</span>
          <div
            className={`ml-auto px-3 py-1 bg-gradient-to-r ${gradient} text-white text-sm font-semibold rounded-full`}
          >
            {percentage.toFixed(0)}%
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r ${gradient} rounded-full transition-all duration-1000 ease-out relative`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          >
            <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Categories</h4>
        {categories.length > 0 ? (
          categories.map((category, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                <span className="text-sm font-medium text-slate-700">{category.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-800">{category.score.toFixed(1)}</span>
                <div className="w-16 bg-slate-200 rounded-full h-2">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      backgroundColor: category.color,
                      width: `${(category.score / 5) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-4 text-slate-500">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No ratings available</p>
          </div>
        )}
      </div>
    </div>
  )
}

const RatingsOverview = observer(() => {
  const { api, store } = useAppContext()
  const me = store.auth.meJson?.uid
  const [description, setDescription] = useState("")
  const [loading, setLoading] = useState(true)

  // Filter ratings
  const valueRatings = store.valueRating.all.filter(
    (value) =>
      value.asJson.rateeId === me &&
      value.asJson.adminApproval !== "Approved for Re-rating" &&
      value.asJson.resubmission !== "To be rated" &&
      value.asJson.resubmission !== "Closed",
  )


  console.log("value ratings",store.valueRating.all);
  
  const leadershipRatings = store.leadershipRating.all
    .filter(
      (value) =>
        value.asJson.rateeId === me &&
        value.asJson.adminApproval !== "Approved for Re-rating" &&
        value.asJson.resubmission !== "To be rated" &&
        value.asJson.resubmission !== "Closed",
    )
    .map((value) => value.asJson)

      console.log("leaders ratings",store.leadershipRating.all);

  const templateRatings = store.templateRating.all
    .filter((value) => value.asJson.rateeId === me && value.asJson.status === "Completed")
    .map((value) => value.asJson)

  // Calculate Values scores
  const valueScores = useMemo(() => {
    const scores: any = {}
    const totals: any = {}
    const counts: any = {}

    valueRatings.forEach((rating) => {
      Object.values(rating.asJson.values).forEach((raterData: any) => {
        Object.entries(raterData.ratings).forEach(([valueName, statements]: any) => {
          if (!totals[valueName]) {
            totals[valueName] = 0
            counts[valueName] = 0
          }
          Object.values(statements).forEach((score: any) => {
            totals[valueName] += score
            counts[valueName] += 1
          })
        })
      })
    })

    Object.keys(totals).forEach((valueName) => {
      scores[valueName] = totals[valueName] / counts[valueName] || 0
    })

    return scores
  }, [valueRatings])

  // Calculate Leadership scores
  const leadershipScores = useMemo(() => {
    const scores: any = {}
    const totals: any = {}
    const counts: any = {}

    leadershipRatings.forEach((rating) => {
      Object.values(rating.criteria).forEach((criteriaData: any) => {
        Object.entries(criteriaData.ratings).forEach(([criteriaName, statements]: any) => {
          if (!totals[criteriaName]) {
            totals[criteriaName] = 0
            counts[criteriaName] = 0
          }
          Object.values(statements).forEach((score: any) => {
            totals[criteriaName] += score
            counts[criteriaName] += 1
          })
        })
      })
    })

    Object.keys(totals).forEach((criteriaName) => {
      scores[criteriaName] = totals[criteriaName] / counts[criteriaName] || 0
    })

    return scores
  }, [leadershipRatings])

  // Calculate Template scores
  const templateScores = useMemo(() => {
    const scores: any = {}
    const totals: any = {}
    const counts: any = {}

    templateRatings.forEach((rating) => {
      Object.values(rating.values).forEach((raterData: any) => {
        Object.entries(raterData.ratings).forEach(([criteriaName, statements]: any) => {
          if (!totals[criteriaName]) {
            totals[criteriaName] = 0
            counts[criteriaName] = 0
          }
          Object.values(statements).forEach((score: any) => {
            totals[criteriaName] += score
            counts[criteriaName] += 1
          })
        })
      })
    })

    Object.keys(totals).forEach((criteriaName) => {
      scores[criteriaName] = totals[criteriaName] / counts[criteriaName] || 0
    })

    return scores
  }, [templateRatings])

  // Calculate overall scores
  const valueOverallScore = useMemo(() => {
    const scores = Object.values(valueScores) as number[]
    return scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : "0.00"
  }, [valueScores])

  const leadershipOverallScore = useMemo(() => {
    const scores = Object.values(leadershipScores) as number[]
    return scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : "0.00"
  }, [leadershipScores])

  const templateOverallScore = useMemo(() => {
    const scores = Object.values(templateScores) as number[]
    return scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : "0.00"
  }, [templateScores])

  // Prepare category data
  const valueCategories = Object.entries(valueScores).map(([name, score], index) => ({
    name,
    score: score as number,
    color: ["#3B82F6", "#8B5CF6", "#10B981", "#F59E0B", "#EF4444"][index % 5],
  }))

  const leadershipCategories = Object.entries(leadershipScores).map(([name, score], index) => ({
    name,
    score: score as number,
    color: ["#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#EF4444"][index % 5],
  }))

  const templateCategories = Object.entries(templateScores).map(([name, score], index) => ({
    name,
    score: score as number,
    color: ["#10B981", "#3B82F6", "#8B5CF6", "#F59E0B", "#EF4444"][index % 5],
  }))

  // Mapping functions (keeping original logic)
  const mapValueToLeadership = (valueRating: IValueRating): ILeadershipRatings => {
    const mappedLeadershipRating: ILeadershipRatings = {
      id: valueRating.id,
      rateeId: valueRating.rateeId,
      date: valueRating.date,
      dimension: valueRating.dimension,
      criteria: {},
      overallRating: valueRating.overallRating,
      isflagged: valueRating.isflagged,
      department: valueRating.department,
      description: valueRating.description,
    }

    Object.keys(valueRating.values).forEach((raterId) => {
      const criteriaName = Object.keys(valueRating.values[raterId].ratings)[0]
      mappedLeadershipRating.criteria[raterId] = {
        additionalComment: valueRating.values[raterId].additionalComment,
        stopDoing: valueRating.values[raterId].stopDoing,
        startDoing: valueRating.values[raterId].startDoing,
        continueDoing: valueRating.values[raterId].continueDoing,
        ratings: {
          [criteriaName]: valueRating.values[raterId].ratings[criteriaName],
        },
      }
    })

    return mappedLeadershipRating
  }

  const mapTemplateToLeadership = (valueRating: ITemplateRating): ILeadershipRatings => {
    const mappedLeadershipRating: ILeadershipRatings = {
      id: valueRating.id,
      rateeId: valueRating.rateeId,
      date: valueRating.dateRequested ?? 0,
      dimension: valueRating.templateId,
      criteria: {},
      overallRating: 0,
      isflagged: valueRating.isflagged,
      department: valueRating.department,
      description: valueRating.description,
      raterId: valueRating.raterId,
      templateId: valueRating.templateId,
      heading: valueRating.heading,
      reasonForRequest: valueRating.reasonForRequest,
      dueDate: valueRating.dueDate,
      values: valueRating.values,
    }

    Object.keys(valueRating.values).forEach((raterId) => {
      const criteriaName = Object.keys(valueRating.values[raterId].ratings)[0]
      mappedLeadershipRating.criteria[raterId] = {
        additionalComment: valueRating.values[raterId].additionalComment,
        stopDoing: valueRating.values[raterId].stopDoing,
        startDoing: valueRating.values[raterId].startDoing,
        continueDoing: valueRating.values[raterId].continueDoing,
        ratings: {
          [criteriaName]: valueRating.values[raterId].ratings[criteriaName],
        },
      }
    })

    return mappedLeadershipRating
  }

  const valueRates = valueRatings.map((valueRating) => mapValueToLeadership(valueRating.asJson))
  const templateRates = templateRatings.map((valueRating) => mapTemplateToLeadership(valueRating))
  const combinedRatings = [...valueRates, ...leadershipRatings, ...templateRates]

useEffect(() => {
  const load = async () => {
    try {
      // 1. Load rating assignments
      await api.ratingAssignments.getAll();
      const activeRating = store.ratingAssignments.all.find(
        (rating) => rating.asJson.isActive === true
      )?.asJson;

      if (activeRating) {
        setDescription(activeRating.description ?? "");
      }

      // 2. Ensure we have both 'me' and 'description'
      if (me && activeRating?.description) {

        console.log("calling api");
        
        await Promise.all([
          api.valueRating.getAll(me, activeRating.description),
          api.leadershipRating.getAll(me, activeRating.description),
          api.templateRating.getAll(),
        ]);
      } else {
        console.warn("No valid 'me' or active rating description found.");
      }
    } catch (error) {
      console.error("Error occurred while loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  load();
}, [api, me]); // Remove store.ratingAssignments.all from dependencies

console.log("ME UID:", me);
console.log("Active Rating Assignments:", store.ratingAssignments.all);
console.log("Selected Description:", description);


  const overallAverage = useMemo(() => {
    const scores = [
      Number.parseFloat(valueOverallScore),
      Number.parseFloat(leadershipOverallScore),
      Number.parseFloat(templateOverallScore),
    ].filter((score) => score > 0)
    return scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : "0.00"
  }, [valueOverallScore, leadershipOverallScore, templateOverallScore])

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <BarChart3 className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Ratings Analysis</h2>
            <p className="text-slate-600">Comprehensive view of your performance ratings</p>
          </div>
        </div>
      </div>

      {/* Overall Summary */}
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-6 border border-blue-100/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
              <Star className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">Overall Performance</h3>
              <p className="text-slate-600">Your combined rating across all categories</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-slate-800">{overallAverage}</div>
            <div className="text-sm text-slate-600">out of 5.00</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-semibold text-slate-700">Values</span>
            </div>
            <div className="text-2xl font-bold text-slate-800">{valueOverallScore}</div>
          </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-semibold text-slate-700">Leadership</span>
            </div>
            <div className="text-2xl font-bold text-slate-800">{leadershipOverallScore}</div>
          </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="h-4 w-4 text-green-600" />
              <span className="text-sm font-semibold text-slate-700">Professional</span>
            </div>
            <div className="text-2xl font-bold text-slate-800">{templateOverallScore}</div>
          </div>
        </div>
      </div>

      {/* Rating Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <RatingCard
          title="Values Ratings"
          subtitle="Core organizational values"
          score={valueOverallScore}
          maxScore={5}
          icon={<Heart className="h-6 w-6" />}
          gradient="from-blue-500 to-indigo-600"
          categories={valueCategories}
          loading={loading}
        />

        <RatingCard
          title="Leadership Ratings"
          subtitle="Leadership competencies"
          score={leadershipOverallScore}
          maxScore={5}
          icon={<Crown className="h-6 w-6" />}
          gradient="from-purple-500 to-violet-600"
          categories={leadershipCategories}
          loading={loading}
        />

        <RatingCard
          title="Professional Competency"
          subtitle="Technical and professional skills"
          score={templateOverallScore}
          maxScore={5}
          icon={<Briefcase className="h-6 w-6" />}
          gradient="from-emerald-500 to-teal-600"
          categories={templateCategories}
          loading={loading}
        />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-lg border border-slate-200/50">
          <div className="flex items-center gap-3 mb-2">
            <Users className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-semibold text-slate-700">Total Ratings</span>
          </div>
          <div className="text-2xl font-bold text-slate-800">
            {valueRatings.length + leadershipRatings.length + templateRatings.length}
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-lg border border-slate-200/50">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <span className="text-sm font-semibold text-slate-700">Completed</span>
          </div>
          <div className="text-2xl font-bold text-slate-800">{combinedRatings.filter((r) => !r.isflagged).length}</div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-lg border border-slate-200/50">
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <span className="text-sm font-semibold text-slate-700">Flagged</span>
          </div>
          <div className="text-2xl font-bold text-slate-800">{combinedRatings.filter((r) => r.isflagged).length}</div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-lg border border-slate-200/50">
          <div className="flex items-center gap-3 mb-2">
            <Zap className="h-5 w-5 text-purple-600" />
            <span className="text-sm font-semibold text-slate-700">Categories</span>
          </div>
          <div className="text-2xl font-bold text-slate-800">
            {Object.keys(valueScores).length +
              Object.keys(leadershipScores).length +
              Object.keys(templateScores).length}
          </div>
        </div>
      </div>

      {/* Flagged Ratings Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertCircle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">Flagged Ratings</h3>
              <p className="text-slate-600">Ratings that require attention or review</p>
            </div>
          </div>
        </div>
             {/* Modern Ratings Table */}
      <ModernRatingsTable data={combinedRatings} description={description} />
      </div>
    </div>
  )
})

export default RatingsOverview
